import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Checkbox, Stack, SearchBox, IconButton, Callout, DirectionalHint, Dropdown, DefaultButton, Separator } from '@fluentui/react';
import { PrimaryButton } from '@fluentui/react/lib/Button';
import { Spinner, SpinnerSize } from '@fluentui/react';
import { syncUsers, getList } from 'store/actions/syncUsersActions';
import ChangeStatusDrawer from '../changestatus/ChangeStatusDrawer';

const UserManageComponent = () => {

    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const dispatch = useDispatch();

    const toggleDrawer = () => setIsDrawerOpen(!isDrawerOpen);

    const { users = [], loading } = useSelector(state => state.syncUsers || { users: [], loading: false });

    const [sortField, setSortField] = useState("name");
    const [sortOrder, setSortOrder] = useState("asc");

    const [searchValue, setSearchValue] = useState('');
    const [searchText, setSearchText] = useState('');
    const [statusFilter, setStatusFilter] = useState(null);

    const [isFilterMenuOpen, setIsFilterMenuOpen] = useState(false);
    const filterButtonRef = useRef(null);
    const [filterStatus, setFilterStatus] = useState(null);

    const [, setSortLoading] = useState(false);

    const [selectedItems, setSelectedItems] = useState([]);
    const [pageIndex, setPageIndex] = useState(0);
    const [allUsers, setAllUsers] = useState([]);
    const [hasMore, setHasMore] = useState(true);
    const scrollContainerRef = useRef(null);

    
    const isLoadingRef = useRef(false);
    const lastScrollTopRef = useRef(0);


    const pageSize = 50;


    const handleRefresh = () => {
        setSearchText('');
        setSearchValue('');
        setFilterStatus(null);
        setStatusFilter(null);
        setPageIndex(0);
        setAllUsers([]);
        setHasMore(true);
        isLoadingRef.current = false;

        
        if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollTop = 0;
        }

        dispatch(getList({
            sort: sortField,
            order: sortOrder,
            pageIndex: 0,
            pageSize,
        }));
    };

    useEffect(() => {
        setPageIndex(0);
        setAllUsers([]);
        setHasMore(true);
        setSortLoading(true);
        isLoadingRef.current = true;

        const params = {
            sort: sortField,
            order: sortOrder,
            filterKey: searchText,
            pageIndex: 0,
            pageSize: pageSize
        };

        
        if (statusFilter !== null && statusFilter !== undefined) {
            params.status = statusFilter;
        }

        dispatch(getList(params)).finally(() => {
            setSortLoading(false);
            isLoadingRef.current = false;
        });
    }, [dispatch, searchText, statusFilter, sortField, sortOrder]);

    
    useEffect(() => {
        if (!users) return;

        const newItems = Array.isArray(users) ? users : users.items || [];

        if (newItems.length === 0) {
            isLoadingRef.current = false;
            return;
        }

        const isRefresh = pageIndex === 0;

        setAllUsers(prevUsers => {
            if (isRefresh) {
                
                return newItems;
            } else {
                
                const existingIds = new Set(
                    prevUsers.map(user => user.id || user.email)
                );

                const uniqueNewUsers = newItems.filter(user => {
                    const userId = user.id || user.email;
                    return !existingIds.has(userId);
                });

                return [...prevUsers, ...uniqueNewUsers];
            }
        });

        setHasMore(newItems.length === pageSize);
        isLoadingRef.current = false;
    }, [users, pageIndex, pageSize]); 

    const loadMoreUsers = useCallback(() => {
        
        if (isLoadingRef.current || !hasMore || loading) {
            return;
        }

        isLoadingRef.current = true;
        const nextPage = pageIndex + 1;
        setPageIndex(nextPage);

        const params = {
            sort: sortField,
            order: sortOrder,
            filterKey: searchText,
            pageIndex: nextPage,
            pageSize: pageSize
        };

        if (statusFilter !== null && statusFilter !== undefined) {
            params.status = statusFilter;
        }

        dispatch(getList(params));
    }, [pageIndex, dispatch, sortField, sortOrder, searchText, statusFilter, pageSize, hasMore, loading]);

    const handleScroll = useCallback((e) => {
        const { scrollTop, scrollHeight, clientHeight } = e.target;

        
        const isScrollingDown = scrollTop > lastScrollTopRef.current;
        lastScrollTopRef.current = scrollTop;

       
        if (isScrollingDown && scrollHeight - scrollTop <= clientHeight * 1.2) {
            loadMoreUsers();
        }
    }, [loadMoreUsers]);

    const handleStatusChanged = (userIds, newStatus) => {
        setAllUsers(prevUsers => {
            const updatedUsers = prevUsers.map(user => {
                const userId = user.id || user.email;
                if (userIds.includes(userId)) {
                    return { ...user, status: newStatus };
                }
                return user;
            });

            if (statusFilter !== null && statusFilter !== undefined) {
                return updatedUsers.filter(user => user.status === statusFilter);
            }

            return updatedUsers;
        });

        if (statusFilter !== null && statusFilter !== undefined) {
            setSelectedItems(prev =>
                prev.filter(id => !userIds.includes(id))
            );
        }
    };

    const handleSyncFromAAD = async () => {
        setSortLoading(true);
        isLoadingRef.current = true;
        try {
            await dispatch(syncUsers(`/users/sync`));

           
            setSearchValue('');
            setSearchText('');
            setFilterStatus(null);
            setStatusFilter(null);
            setPageIndex(0);
            setAllUsers([]);
            setHasMore(true);

            const params = {
                sort: sortField,
                order: sortOrder,
                pageIndex: 0,
                pageSize: pageSize
            };

            await dispatch(getList(params));
        } finally {
            setSortLoading(false);
            isLoadingRef.current = false;
        }
    };

    const handleSort = (field) => {
        let newSortOrder = "asc";
        if (sortField === field) {
            newSortOrder = sortOrder === "asc" ? "desc" : "asc";
        }

        setSortField(field);
        setSortOrder(newSortOrder);

        
        if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollTop = 0;
        }
    };

    const formatPhoneNumber = (phone) => {
        if (!phone) return '-';

        
        const cleaned = phone.replace(/\D/g, '');

        if (cleaned.length === 10) {
            return `${cleaned.substring(0, 3)}-${cleaned.substring(3, 6)}-${cleaned.substring(6)}`;
        }

        return phone;
    };

    const handleHeaderCheckboxChange = (_, checked) => {
        if (checked) {
            const allIds = allUsers.map(user => user.id || user.email);
            setSelectedItems(allIds);
        } else {
            setSelectedItems([]);
        }
    };

    const handleRowCheckboxChange = (userId, checked) => {
        if (checked) {
            setSelectedItems(prev => [...prev, userId]);
        } else {
            setSelectedItems(prev => prev.filter(id => id !== userId));
        }
    };


    const isAllSelected = allUsers.length > 0 && selectedItems.length === allUsers.length;

    const statusOptions = [
        { key: 2, text: 'Active' },
        { key: 3, text: 'Inactive' },
    ];

    const handleApplyFilters = () => {
        setSearchText(searchValue);
        setStatusFilter(filterStatus);
        setPageIndex(0);
        setAllUsers([]);
        setHasMore(true);
        isLoadingRef.current = false;
        setIsFilterMenuOpen(false);

        if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollTop = 0;
        }

        const params = {
            sort: sortField,
            order: sortOrder,
            filterKey: searchValue || '',
            pageIndex: 0,
            pageSize: pageSize
        };

        if (filterStatus !== null && filterStatus !== undefined) {
            params.status = filterStatus;
        }

        dispatch(getList(params));
    };

    const handleClearFilters = () => {
        setSearchValue('');
        setFilterStatus(null);
        setSearchText('');
        setStatusFilter(null);
        setPageIndex(0);
        setAllUsers([]);
        setHasMore(true);
        isLoadingRef.current = false;
        setIsFilterMenuOpen(false);

        
        if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollTop = 0;
        }

        // Immediately fetch all records without filters
        dispatch(getList({
            sort: sortField,
            order: sortOrder,
            pageIndex: 0,
            pageSize: pageSize,
        }));
    };

    const handleCancelFilters = () => {
        setSearchValue(searchText);
        setFilterStatus(statusFilter);
        setIsFilterMenuOpen(false);
    };

    // Check if any filters are applied
    const hasActiveFilters = searchText || statusFilter !== null;

    return (
        <div className="teams-card">
            <div className='page-action-header'>
                <Stack horizontal tokens={{ childrenGap: 16 }} styles={{ root: { width: "100%", display: "flex", alignItems: "center" } }}>
                    <Stack horizontal styles={{ root: { position: 'relative' } }}>
                        <SearchBox
                            style={{ width: '300px' }}
                            placeholder="Search here"
                            value={searchValue}
                            onChange={(_, newValue) => {
                                const limited = (newValue ?? '').slice(0, 40);
                                setSearchValue(limited);
                                if ((newValue ?? '').trim() === '') {
                                    setSearchValue('');
                                    handleRefresh();
                                }
                            }}
                            onSearch={newValue => setSearchText(newValue || '')}
                            showIcon={true}
                            disableAnimation={true}
                            styles={{
                                clearButton: { display: 'none' }
                            }}
                        />
                        <div ref={filterButtonRef} style={{ position: 'relative' }}>
                            <IconButton
                                iconProps={{ iconName: 'Filter' }}
                                title="Filter"
                                ariaLabel="Filter"
                                onClick={() => setIsFilterMenuOpen(!isFilterMenuOpen)}
                                styles={{
                                    root: {
                                        position: 'absolute',
                                        right: '8px',
                                        top: '50%',
                                        transform: 'translateY(-50%)',
                                        zIndex: 1
                                    }
                                }}
                            />
                            {hasActiveFilters && (
                                <span
                                    style={{
                                        position: 'absolute',
                                        top: '8px',
                                        right: '10px',
                                        width: '8px',
                                        height: '8px',
                                        backgroundColor: '#dc2626',
                                        borderRadius: '50%',
                                        zIndex: 2,
                                        border: '1px solid white'
                                    }}
                                />
                            )}
                        </div>
                    </Stack>
                </Stack>

                <div className='d-flex'>
                    {selectedItems.length > 0 && (
                        <PrimaryButton
                            text="Change Status"
                            type="button"
                            onClick={toggleDrawer}
                        />
                    )}

                    <PrimaryButton
                        text="Sync From AAD"
                        type="button"
                        onClick={handleSyncFromAAD}
                    />
                </div>
            </div>

            <div
                ref={scrollContainerRef}
                onScroll={handleScroll}
                className="table-container"
            >
                <table>
                    <thead>
                        <tr>
                            <th className="w-40 cursor-pointer">
                                <div className="d-flex" onClick={() => handleSort("name")}>
                                    <div onClick={(e) => e.stopPropagation()}>
                                        <Checkbox
                                            checked={isAllSelected}
                                            onChange={handleHeaderCheckboxChange}
                                        />
                                    </div>
                                    <span>Name {sortField === "name" && (sortOrder === "asc" ? "↑" : "↓")}</span>
                                </div>
                            </th>
                            <th className="cursor-pointer" onClick={() => handleSort("email")}>
                                Email {sortField === "email" && (sortOrder === "asc" ? "↑" : "↓")}
                            </th>
                            <th className="cursor-pointer" onClick={() => handleSort("phone")}>
                                Phone {sortField === "phone" && (sortOrder === "asc" ? "↑" : "↓")}
                            </th>
                            <th className="cursor-pointer" onClick={() => handleSort("status")}>
                                Status {sortField === "status" && (sortOrder === "asc" ? "↑" : "↓")}
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {allUsers && allUsers.length > 0 ? (
                            allUsers.map((user, index) => {
                                const userId = user.id || user.email;
                                const uniqueKey = `${userId}-${index}`;
                                return (
                                    <tr key={uniqueKey}>
                                        <td>
                                            <div className='d-flex'>
                                                <div>
                                                    <Checkbox
                                                        checked={selectedItems.includes(userId)}
                                                        onChange={(_, checked) => handleRowCheckboxChange(userId, checked)}
                                                    />
                                                </div>
                                                <div >
                                                    {user.name || user.displayName || 'N/A'}
                                                </div>
                                            </div>
                                        </td>
                                        <td>{user.email}</td>
                                        <td>{formatPhoneNumber(user.phone)}</td>
                                        <td>
                                            <div
                                                className={`chip ${user.status === 2 ? "active" : user.status === 3 ? "danger" : user.status === 6 ? "completed" : ""}`}
                                            >
                                                {user.status === 2 ? "Active" : user.status === 3 ? "Inactive" : ""}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })
                        ) : (
                            !loading && allUsers.length === 0 && (
                                <tr>
                                    <td colSpan="4" className="text-center">
                                        No users found. Click "Sync From AAD" to load users.
                                    </td>
                                </tr>
                            )
                        )}
                    </tbody>
                </table>

                {/* Loading indicator for subsequent pages */}
                {loading && pageIndex > 0 && (
                    <div style={{ textAlign: 'center', padding: '20px' }}>
                        <Spinner size={SpinnerSize.medium} label="Loading more users..." />
                    </div>
                )}
            </div>

            {isDrawerOpen && (
                <ChangeStatusDrawer
                    closeDrawer={toggleDrawer}
                    selectedUserIds={selectedItems}
                    clearSelection={() => setSelectedItems([])}
                    onStatusChanged={handleStatusChanged}
                />
            )}

            {isFilterMenuOpen && (
                <Callout
                    className="filter-callout"
                    target={filterButtonRef.current}
                    onDismiss={() => setIsFilterMenuOpen(false)}
                    directionalHint={DirectionalHint.bottomRightEdge}
                    isBeakVisible={false}
                >
                    <Stack tokens={{ childrenGap: 16 }}>
                        <SearchBox
                            placeholder="Search here"
                            value={searchValue}
                            onChange={(_, newValue) => {
                                const limited = (newValue ?? '').slice(0, 40);
                                setSearchValue(limited);
                                if ((newValue ?? '').trim() === '') {
                                    setSearchValue('');
                                    handleRefresh();
                                }
                            }}
                        />

                        <Dropdown
                            placeholder="Status"
                            label="Status"
                            options={statusOptions}
                            selectedKey={filterStatus}
                            onChange={(_, option) => setFilterStatus(option?.key)}
                        />

                        <Separator />

                        <Stack horizontal tokens={{ childrenGap: 10 }} styles={{ root: { justifyContent: 'space-between' } }}>
                            <DefaultButton
                                className="btn-sm"
                                text="Clear All"
                                onClick={handleClearFilters}
                            />
                            <Stack horizontal tokens={{ childrenGap: 10 }}>
                                <DefaultButton
                                    className="btn-sm"
                                    text="Cancel"
                                    onClick={handleCancelFilters}
                                />
                                <PrimaryButton
                                    className="btn-sm"
                                    text="Apply"
                                    onClick={handleApplyFilters}
                                />
                            </Stack>
                        </Stack>
                    </Stack>
                </Callout>
            )}
        </div>
    );
};

export default UserManageComponent;